import { useEffect, useMemo, useState } from 'react';
import { gql, useQuery } from '@apollo/client';

import { adminClient } from 'api/index';

const SEARCH_QUERY = gql`
  query AppShellSearch($term: String!) {
    search(term: $term) {
      items {
        kind
        title
        subtitle
        meta
        url
      }
    }
  }
`;

// Mirrors AdminSearch::MIN_TERM — asking for one character is a round trip that can only come back
// with noise.
const MIN_TERM = 2;
// Long enough that typing an order number is one query rather than six.
const DEBOUNCE_MS = 220;

// The order the groups appear in, and the icon each one borrows from the rail.
const GROUPS = [
  { kind: 'ORDER', label: 'Órdenes', icon: 'package' },
  { kind: 'DISPATCH_CONTROL', label: 'Despachos', icon: 'truck' },
  { kind: 'CUSTOMER_TICKET', label: 'RMA', icon: 'inbox' },
  { kind: 'COURIER', label: 'Mensajeros', icon: 'user' },
];

// Turns what is typed in the palette into record hits. The server decides which kinds the caller may
// see, so a group that comes back empty is either no match or no permission — the palette cannot and
// need not tell them apart.
export default function useEntitySearch({ query, enabled }) {
  const [term, setTerm] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setTerm(query.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const skip = !enabled || term.length < MIN_TERM;

  const { data, loading } = useQuery(SEARCH_QUERY, {
    client: adminClient,
    skip,
    variables: { term },
    fetchPolicy: 'cache-and-network',
  });

  const entityGroups = useMemo(() => {
    // Apollo keeps the last result once the query is skipped, which would leave the previous search
    // on screen after the term is cleared.
    const items = (skip ? [] : data?.search?.items) || [];

    return GROUPS
      .map(({ kind, label, icon }) => ({
        label,
        items: items
          .filter((item) => item.kind === kind)
          .map((item) => ({
            name: item.title, detail: item.subtitle, meta: item.meta, url: item.url, icon,
          })),
      }))
      .filter((group) => group.items.length);
  }, [data, skip]);

  return { entityGroups, loadingEntities: !skip && loading };
}
